import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import { MessagesPanel } from '../components/clientPortal/MessagesPanel';
import { BuyerOverviewStrip, useBuyerPortalData } from '../components/clientPortal/BuyerPortalPanels';
import { formatBuyerCriteriaLine } from '../utils/buyerProfileDisplay';
import { listingStatusLabel } from '../components/clientPortal/sellerPortalLabels';
import { clientAuthConfig } from '../utils/clientAuthConfig';
import api from '../services/api';
import CreaTrademarkNotice from '../components/CreaTrademarkNotice';
import './ClientPortal.css';

function PasswordCard({ slug }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== again) {
      setMsg({ ok: false, text: 'New passwords do not match.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/clients/me/password', {
        current_password: current,
        new_password: next,
      }, clientAuthConfig(slug));
      setCurrent('');
      setNext('');
      setAgain('');
      setMsg({ ok: true, text: 'Password changed.' });
    } catch (err) {
      setMsg({
        ok: false,
        text: err?.response?.data?.error || 'Could not change the password.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="client-portal__panel">
      <h2>Password</h2>
      <p className="client-portal__panel-desc">Change the password you use to sign in here.</p>
      <form className="client-form" onSubmit={submit}>
        <label>
          Current password
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </label>
        <label>
          New password
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={8} required />
        </label>
        <label>
          New password again
          <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" minLength={8} required />
        </label>
        {msg && (
          <p className={msg.ok ? 'client-form__success' : 'client-form__error'}>{msg.text}</p>
        )}
        <div className="client-portal__actions">
          <button type="submit" className="client-form__submit" disabled={busy}>
            {busy ? 'Saving...' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
}

export default function ClientOverviewPage() {
  const { slug } = useParams();
  const { client, reload } = useClientAuth();
  const showBuying = client.client_role === 'buyer' || client.client_role === 'both';
  const showSelling = client.client_role === 'seller' || client.client_role === 'both';
  // Hook is only meaningful for buyers, but React requires hooks to run
  // unconditionally — the returned data is simply unused when !showBuying.
  const buyerData = useBuyerPortalData(slug, client, reload);
  const aiName = client.agent?.assistant_name || 'Ava';

  const criteriaLine = showBuying ? formatBuyerCriteriaLine(client.buyer_profile) : null;
  const sellerSummary = showSelling
    ? [client.seller_profile?.property_address, client.seller_profile?.property_city].filter(Boolean).join(', ')
      || listingStatusLabel(client.seller_profile?.listing_status, aiName)
    : null;

  return (
    <div className="client-portal-page">
      <header className="client-portal-page__header">
        <div className="client-portal-page__badges">
          {showBuying && <span className="client-portal__role-badge client-portal__role-badge--buyer">Buying</span>}
          {showSelling && <span className="client-portal__role-badge client-portal__role-badge--seller">Selling</span>}
        </div>
        <h1>Hi, {client.first_name}</h1>
        <p>Your workspace with {client.agent?.first_name} {client.agent?.last_name} · {client.agent?.brokerage}</p>
      </header>

      <MessagesPanel client={client} agentSlug={slug} />

      {showBuying && <BuyerOverviewStrip client={client} buyerData={buyerData} />}

      <div className="client-portal-page__summary-row">
        {showBuying && (
          <Link to={`/client/${slug}/buying`} className="client-portal-page__summary-card">
            <h3>Buying</h3>
            <p>{criteriaLine || `Chat with ${aiName} to set your budget & areas`}</p>
            <span className="client-portal-page__summary-link">View details →</span>
          </Link>
        )}
        {showSelling && (
          <Link to={`/client/${slug}/selling`} className="client-portal-page__summary-card">
            <h3>Selling</h3>
            <p>{sellerSummary}</p>
            <span className="client-portal-page__summary-link">View details →</span>
          </Link>
        )}
      </div>

      <PasswordCard slug={slug} />

      <CreaTrademarkNotice />
    </div>
  );
}
